import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useMe, useUpdateMe } from '../api/hooks'
import { uploadAvatar } from '../api/client'
import { Input, Textarea, PageSpinner } from '../components/ui'

export function Onboarding() {
  const { t } = useI18n()
  const nav = useNavigate()
  const { data: me, isLoading } = useMe()
  const updateMe = useUpdateMe()

  const [displayName, setDisplayName] = useState('')
  const [about, setAbout] = useState('')
  const [country, setCountry] = useState('')
  const [city, setCity] = useState('')
  const [memory, setMemory] = useState('')
  const [avatarKey, setAvatarKey] = useState<string | undefined>()
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  if (isLoading) return <PageSpinner />
  // already onboarded → straight to app
  if (me?.onboarded) {
    nav('/app', { replace: true })
    return null
  }

  const onAvatar = async (file: File) => {
    setAvatarPreview(URL.createObjectURL(file))
    const key = await uploadAvatar(file)
    setAvatarKey(key)
  }

  const submit = async () => {
    // Say WHY the form can't continue — a silently disabled button strands people.
    if (!country.trim()) {
      setErr(t('errors.required', { fields: t('onboarding.country') }))
      return
    }
    setErr('')
    setBusy(true)
    try {
      await updateMe.mutateAsync({
        displayName: displayName || me?.displayName,
        about,
        country,
        city,
        firstHorseMemory: memory,
        avatarKey,
        onboarded: true,
      } as never)
      nav('/app', { replace: true })
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto max-w-xl px-5 py-10">
      <div className="card p-8">
        <span className="chip mb-3">🐎 {t('onboarding.badge')}</span>
        <h1 className="text-2xl font-extrabold tracking-tight">{t('onboarding.title')}</h1>
        <p className="mt-1 text-brand-600 dark:text-brand-300">{t('onboarding.subtitle')}</p>

        <div className="mt-6 flex items-center gap-4">
          <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-full bg-brand-100 dark:bg-brand-800 text-3xl">
            {avatarPreview ? <img src={avatarPreview} alt="" className="h-full w-full object-cover" /> : '👤'}
          </div>
          <label className="btn-ghost cursor-pointer !py-2 text-sm">
            {t('onboarding.addPhoto')}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onAvatar(e.target.files[0])} />
          </label>
        </div>

        <div className="mt-6 space-y-4">
          <Input label={t('onboarding.name')} value={displayName} onChange={setDisplayName} placeholder={me?.displayName} />
          <Textarea label={t('onboarding.about')} value={about} onChange={setAbout} placeholder={t('onboarding.aboutPlaceholder')} />
          <div className="grid grid-cols-2 gap-4">
            <Input label={t('onboarding.country')} value={country} onChange={setCountry} required />
            <Input label={t('onboarding.city')} value={city} onChange={setCity} />
          </div>
          <Textarea label={t('onboarding.memory')} value={memory} onChange={setMemory} placeholder={t('onboarding.memoryPlaceholder')} rows={3} />
        </div>

        {err && <p className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm font-semibold text-red-700 dark:text-red-300">{err}</p>}
        <button disabled={busy} onClick={submit} className="btn-primary mt-6 w-full disabled:opacity-60">
          {busy ? `${t('common.save')}…` : t('onboarding.cta')}
        </button>
      </div>
    </main>
  )
}
